import { Navigate, Outlet, useLocation } from "react-router"

import { homeFor } from "@/features/auth/session"
import { ForbiddenPage } from "@/app/error-pages"
import { Skeleton } from "@/components/ui/skeleton"
import { useAuth } from "@/lib/auth"
import { can, isSuperOwner, type Permission } from "@/lib/permissions"

function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center" aria-busy="true">
      <Skeleton className="h-10 w-48" />
    </div>
  )
}

function useSignedIn() {
  const auth = useAuth()
  const location = useLocation()
  if (auth.status === "loading") return { el: <Splash /> }
  if (auth.status === "anonymous") {
    const next = encodeURIComponent(location.pathname + location.search)
    return { el: <Navigate to={`/login?next=${next}`} replace /> }
  }
  return { user: auth.user }
}

/** Owner routes; optional permission (renders 403 rather than redirecting). */
export function RequireOwner({ perm, superOnly }: { perm?: Permission; superOnly?: boolean }) {
  const { el, user } = useSignedIn()
  if (!user) return el
  if (user.role !== "owner") return <ForbiddenPage home="/portal" />
  if (superOnly && !isSuperOwner(user)) return <ForbiddenPage home="/" />
  if (perm && !can(user, perm)) return <ForbiddenPage home="/" />
  return <Outlet />
}

export function RequireCoordinator() {
  const { el, user } = useSignedIn()
  if (!user) return el
  if (user.role !== "coordinator") return <ForbiddenPage home="/" />
  return <Outlet />
}

/** Public auth pages redirect signed-in users home. */
export function PublicOnly() {
  const auth = useAuth()
  if (auth.status === "loading") return <Splash />
  if (auth.status === "authenticated") return <Navigate to={homeFor(auth.user.role)} replace />
  return <Outlet />
}
